import { DateTime, Duration } from 'luxon';

import {
  AppointmentIcon,
  FlexibleIcon,
  OnDemandIcon,
  PickupIcon,
} from 'assets/v2/icons';

import { PUDO_LOCATION_KEYS } from './pudoLocationKeys';

/**
 * The set of all valid trip booking type codes.
 */
export const TRIP_BOOKING_TYPE_CODES = Object.freeze({
  ARRIVE_BY: 'ARRIVE_BY',
  SCHEDULED: 'SCHEDULED',
  ONDEMAND: 'ONDEMAND',
  WILLCALL: 'WILLCALL',
});

/**
 * The user-friendly labels for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_LABELS = Object.freeze({
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: 'Appointment',
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: 'Pickup',
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: 'On Demand',
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: 'Will Call',
});

/**
 * The user-friendly labels for the trip booking types' time.
 */
export const TRIP_BOOKING_TYPE_TIME_LABELS = Object.freeze({
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: `${TRIP_BOOKING_TYPE_LABELS.ARRIVE_BY} Time`,
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: `${TRIP_BOOKING_TYPE_LABELS.SCHEDULED} Time`,
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: 'Pickup Time',
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: 'Pickup Time',
});

/**
 * The user-friendly descriptions for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_DESCRIPTIONS = Object.freeze({
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]:
    'Time the member needs to arrive at the medical facility',
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]:
    'Planned pickup time for the member from their location',
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: 'Trip is needed as soon as possible',
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]:
    ' Pickup time will be confirmed later by the member or medical facility',
});

/**
 * The icons for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_ICONS = Object.freeze({
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: AppointmentIcon,
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: PickupIcon,
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: OnDemandIcon,
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: FlexibleIcon,
});

/**
 * The PUDO DateTime keys for determining which DateTime field to display for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_DATETIME_KEYS = Object.freeze({
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: `${PUDO_LOCATION_KEYS.DROP_OFF}DateTime`,
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: `${PUDO_LOCATION_KEYS.PICKUP}DateTime`,
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: `${PUDO_LOCATION_KEYS.PICKUP}DateTime`,
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: `${PUDO_LOCATION_KEYS.PICKUP}DateTime`,
});

/**
 * Default pickup date value for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_DEFAULT_DATE = {
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: () => null,
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: () => null,
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: () =>
    DateTime.fromObject({
      day: DateTime.now().day,
      month: DateTime.now().month,
      year: DateTime.now().year,
    }),
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: () =>
    DateTime.fromObject({ hour: 23, minute: 59 }),
};

/**
 * Default pickup DateTime value for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_DEFAULT_DATETIME = {
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: () => null,
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: () => null,
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: () =>
    DateTime.fromObject({
      hour: DateTime.now().plus(Duration.fromObject({ minutes: 15 })).hour,
      minute: DateTime.now().plus(Duration.fromObject({ minutes: 15 })).minute,
      second: 0,
      millisecond: 0,
    }),
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: () =>
    DateTime.fromObject({ hour: 23, minute: 59 }),
};

/**
 * Default pickup time value for the trip booking types.
 */
export const TRIP_BOOKING_TYPE_DEFAULT_TIME = {
  [TRIP_BOOKING_TYPE_CODES.SCHEDULED]: () =>
    DateTime.fromObject({ hour: 0, minute: 0 }).toFormat('h:mm a'),
  [TRIP_BOOKING_TYPE_CODES.ARRIVE_BY]: () =>
    DateTime.fromObject({ hour: 0, minute: 0 }).toFormat('h:mm a'),
  [TRIP_BOOKING_TYPE_CODES.ONDEMAND]: () =>
    DateTime.fromObject({
      hour: DateTime.now().plus(Duration.fromObject({ minutes: 15 })).hour,
      minute: DateTime.now().plus(Duration.fromObject({ minutes: 15 })).minute,
      second: 0,
      millisecond: 0,
    }).toFormat('h:mm a'),
  [TRIP_BOOKING_TYPE_CODES.WILLCALL]: () =>
    DateTime.now().endOf('day').toFormat('h:mm a'),
};
